/**
 * What every framework adapter needs and none of them should own twice.
 *
 * An adapter is a translation onto the core, not a second implementation of
 * it. The three things a translation actually has to solve are here:
 *
 * 1. **Meta by identity.** The core's default `resolveMeta` reads
 *    `el.dataset`, which stringifies everything. A framework consumer has a
 *    real object already, so the adapter stores it in a `WeakMap` keyed on the
 *    element and installs a `resolveMeta` that reads it back. Predicates then
 *    receive the React prop or the Vue reactive object itself, by identity.
 * 2. **A stable registration.** Framework components re-render constantly and
 *    hand over a new inline `items` arrow every time. The registration reads
 *    the definition through a mutable holder, so a re-render refreshes what
 *    the menu will show without touching the registry.
 * 3. **One shared fallback instance**, so a consumer who mounts no provider
 *    still gets exactly one delegated listener on the document.
 */

import { ContextMenu, createContextMenu, datasetMeta } from '../core/ContextMenu'
import { resolveLabel } from '../core/Resolver'
import type { Item, ItemsResolver, MenuDefinition, Meta } from '../core/types'

/** A definition as an adapter takes it: no selector, plus a real `meta` object. */
export interface AdapterDefinition extends Omit<MenuDefinition, 'on'> {
  /**
   * Passed to predicates, labellers and actions untouched. Storing the object
   * rather than a copy is the point: a later mutation of it is visible on the
   * next open, and identity comparisons in a predicate work.
   */
  meta?: Meta
}

/**
 * The holder a registration reads through.
 *
 * Deliberately the same shape as a React ref and a plain object, so both
 * adapters can pass what they already have.
 */
export interface DefinitionRef {
  current: AdapterDefinition
}

/**
 * Element to meta. A `WeakMap` so an element that leaves the document takes its
 * entry with it even if an adapter forgets to dispose.
 */
const META = new WeakMap<HTMLElement, Meta>()

/** Store, replace or drop the meta object an element's menu will receive. */
export function setElementMeta(el: HTMLElement, meta: Meta | undefined): void {
  if (meta === undefined) {
    META.delete(el)

    return
  }

  META.set(el, meta)
}

/** The stored meta for an element, or undefined when it has none. */
export function elementMeta(el: HTMLElement): Meta | undefined {
  return META.get(el)
}

/** Normalise `class` so two renders that mean the same thing compare equal. */
function classList(value: string | string[] | undefined): string[] {
  if (value === undefined) return []

  return (Array.isArray(value) ? value : value.split(' ')).filter((entry) => entry.length > 0)
}

/**
 * The parts of a definition that a live registration cannot express.
 *
 * `items`, `meta`, `resolveMeta` and the header text are read at open time, so
 * they can change freely. `class`, `variant`, `offset`, `priority` and whether
 * a header exists at all are copied into the registry when it is created, so a
 * change to one of those has to re-register. This signature is what an adapter
 * compares to decide that, and it is stable across renders that changed
 * nothing.
 */
export function definitionSignature(def: AdapterDefinition): string {
  return JSON.stringify([
    classList(def.class),
    def.variant ?? null,
    def.offset ?? null,
    def.priority ?? null,
    def.header !== undefined,
  ])
}

/**
 * Register one element's menu on `instance`, reading everything that may change
 * through `ref`.
 *
 * Uses `attach`, so this is the ordinary delegated path with a generated
 * selector. No adapter adds a `contextmenu` listener of its own: there is still
 * exactly one on the root however many elements are bound.
 */
function buildDefinition(ref: DefinitionRef): Omit<MenuDefinition, 'on'> {
  const definition: Omit<MenuDefinition, 'on'> = {
    // A forwarder, never the array itself, so the newest items win without the
    // registration being rebuilt.
    items: (meta, ctx): Item[] | Promise<Item[]> => {
      const current: Item[] | ItemsResolver = ref.current.items

      return typeof current === 'function' ? current(meta, ctx) : current
    },
    resolveMeta: (element, event): Meta => {
      const custom = ref.current.resolveMeta

      if (custom !== undefined) return custom(element, event)

      // The whole reason the adapters exist: the stored object, by identity.
      // The per element entry wins, then the definition's own `meta`, which is
      // what a delegated selector registration has, then the core's dataset.
      // That last fallback is the core's own `datasetMeta` and not a second
      // spread of `element.dataset`: the adapters register through `attach`, so
      // every element they bind carries a `data-ctx-N` marker that a raw spread
      // would hand straight to a predicate.
      return elementMeta(element) ?? ref.current.meta ?? datasetMeta(element)
    },
    class: ref.current.class,
    variant: ref.current.variant,
    offset: ref.current.offset,
    priority: ref.current.priority,
  }

  // Presence is fixed for the life of the registration, the text is not: an
  // always-present forwarder would give a menu with no header an empty one.
  if (ref.current.header !== undefined) {
    definition.header = (meta, ctx): string => resolveLabel(ref.current.header ?? '', meta, ctx)
  }

  return definition
}

/** Wrap a disposer so calling it twice is safe. */
function once(dispose: () => void): () => void {
  let disposed = false

  return () => {
    if (disposed) return

    disposed = true
    dispose()
  }
}

export function bindElement(
  instance: ContextMenu,
  el: HTMLElement,
  ref: DefinitionRef,
): () => void {
  setElementMeta(el, ref.current.meta)

  const dispose = instance.attach(el, buildDefinition(ref))

  return once(() => {
    dispose()
    META.delete(el)
  })
}

/**
 * Register a delegated selector rather than one element.
 *
 * The same forwarding definition, so a composable that binds by selector gets
 * the live `items` and the real `meta` object that a bound element gets. There
 * is no element to key the `WeakMap` on, so `meta` comes from the definition
 * and is shared by every element the selector matches.
 */
export function bindSelector(instance: ContextMenu, on: string, ref: DefinitionRef): () => void {
  return once(instance.register({ ...buildDefinition(ref), on }))
}

/** Where an imperative open should point when the caller gave no coordinates. */
function pointOf(el: HTMLElement, source: Event | null | undefined): { x: number; y: number } {
  if (source instanceof MouseEvent) return { x: source.clientX, y: source.clientY }

  const rect = el.getBoundingClientRect()

  return { x: rect.left, y: rect.top }
}

/**
 * Open the menu for `el` imperatively.
 *
 * This dispatches a real `contextmenu` event on the element rather than calling
 * `instance.open` directly, so an imperative open takes the same delegated path
 * as a right-click: the same registry match, the same deepest-match rule, the
 * same event sequence. An adapter therefore cannot drift from the core here.
 *
 * When the caller passes their own `contextmenu` event, its default is
 * suppressed, because a consumer wiring `open(e)` into an `onContextMenu`
 * handler wants their menu and not the browser's.
 */
export function requestOpen(el: HTMLElement, source?: Event | null): void {
  const point = pointOf(el, source)

  if (source?.type === 'contextmenu' && source.cancelable) source.preventDefault()

  el.dispatchEvent(
    new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      clientX: point.x,
      clientY: point.y,
    }),
  )
}

/**
 * Whether this runtime has a DOM to build a menu against.
 *
 * The whole library is a document listener and an element in the top layer, so
 * on a server there is nothing for it to be. Every entry point that could be
 * reached during a server render asks this first.
 */
export function canUseDom(): boolean {
  return typeof document !== 'undefined'
}

/** The instance used when a consumer mounts no provider or plugin. */
let fallback: ContextMenu | null = null

/**
 * The module level fallback instance, created on first use and never destroyed.
 *
 * Lazy rather than eager: importing an adapter must not install a document
 * listener in a consumer who only wanted the types. Every adapter shares this
 * one instance, so a page mixing React and Vue islands still has a single
 * delegated listener. A provider or plugin mounted later takes precedence for
 * everything beneath it; this instance stays alive for whatever is still bound
 * to it.
 *
 * Null on a server, and that is the contract rather than a degraded mode. The
 * laziness alone was never enough: React calls a hook during the render phase,
 * so a server render reached the constructor and died on `document`. Returning
 * null means a server render produces no instance at all, and the client's
 * first render creates the one real instance.
 */
export function sharedInstance(): ContextMenu | null {
  if (!canUseDom()) return null

  fallback ??= createContextMenu()

  return fallback
}
